import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";

/** Encode each path segment individually while preserving / separators. */
export const encodePath = (p: string) =>
  p.split("/").map(encodeURIComponent).join("/");

// --- Types ---

export interface WorkspaceEntry {
  name: string;
  type: "file" | "directory";
  size?: number;
  last_modified?: string;
}

export interface WorkspaceListResponse {
  path: string;
  directories: WorkspaceEntry[];
  files: WorkspaceEntry[];
}

// --- Queries ---

const listWorkspaceFiles = (path: string): Promise<WorkspaceListResponse> => {
  return axios
    .get(`${nuclearn_api_url}/workspace/files`, { params: { path } })
    .then((res) => res.data);
};

export const useWorkspaceFiles = (path: string = "") => {
  return useQuery(["workspace-files", path], () => listWorkspaceFiles(path), {
    refetchOnWindowFocus: false,
  });
};

// --- Mutations ---

const uploadWorkspaceFile = async ({
  path,
  file,
}: {
  path: string;
  file: File;
}) => {
  const filePath = path ? `${path}/${file.name}` : file.name;
  const formData = new FormData();
  formData.append("file", file);
  return axios
    .put(
      `${nuclearn_api_url}/workspace/files/${encodePath(filePath)}`,
      formData,
    )
    .then((res) => res.data);
};

export const useUploadWorkspaceFile = () => {
  const queryClient = useQueryClient();
  return useMutation(uploadWorkspaceFile, {
    onSuccess: () => {
      queryClient.invalidateQueries("workspace-files");
    },
  });
};

const deleteWorkspaceFile = async (filePath: string) => {
  return axios
    .delete(`${nuclearn_api_url}/workspace/files/${encodePath(filePath)}`)
    .then((res) => res.data);
};

export const useDeleteWorkspaceFile = () => {
  const queryClient = useQueryClient();
  return useMutation(deleteWorkspaceFile, {
    onSuccess: () => {
      queryClient.invalidateQueries("workspace-files");
    },
  });
};

// --- Download (uses axios so the auth interceptor handles the token) ---

export async function downloadWorkspaceFile(filePath: string): Promise<void> {
  const response = await axios.get(
    `${nuclearn_api_url}/workspace/files/${encodePath(filePath)}`,
    { responseType: "blob" },
  );

  const blob = new Blob([response.data]);
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filePath.split("/").pop() ?? "download";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}
